import type { BaseProduct } from '$lib/js/elasticsearch/types'
import type { Settings, Universe } from '$lib/js/strapi/types'
import { PUBLIC_MAGENTO_ENDPOINT } from '$env/static/public'
import { page } from '$app/state'
import { goto, pushState, replaceState } from '$app/navigation'
import { get } from 'svelte/store'
import { storeActiveWishlist, storeCustomerToken, storeLoggedUser, storeWishlists } from '$lib/js/stores'
import type { CustomerToken } from '$lib/js/magento/types'
import { getIntSearchParam, getSearchParam } from './handlers'
import {
	getPercentageSaved,
	getProductFinalPrice,
	getProductFinalPriceWithTax,
	getProductRegularPrice,
	getProductRegularPriceWithTax,
	getProductRetailPrice,
	getProductRetailPriceWithTax,
	getProductSpecialPrice,
	getProductSpecialPriceWithTax,
	getProductTierPrice,
	getProductTierPrices,
	getProductTierPriceWithTax,
	getPromotionEndDate,
} from './prices'
import type { SiteScope } from '$lib/js/helpers/i18n'
import { datetimeToDate, formatDatetime, getNextMonthsDate, getTodaysDate, timeAgo } from './dates'
import type { Wishlist } from '../supabase/wishlists'
import { gt } from 'svelte-i18n-lingui'
import type { ProductTag } from '$lib/js/types'

export {
	// handlers
	getIntSearchParam,
	getSearchParam,

	// prices
	getProductFinalPrice,
	getProductFinalPriceWithTax,
	getProductRegularPrice,
	getProductRegularPriceWithTax,
	getProductRetailPrice,
	getProductRetailPriceWithTax,
	getProductSpecialPrice,
	getProductSpecialPriceWithTax,
	getProductTierPrice,
	getProductTierPrices,
	getProductTierPriceWithTax,
	getPromotionEndDate,
	getPercentageSaved,

	// dates
	datetimeToDate,
	formatDatetime,
	timeAgo,
	getTodaysDate,
	getNextMonthsDate,
}

/**
 * Check if an array is not empty.
 */
export function is_set_array(elem: any): boolean {
	return Array.isArray(elem) && elem.length > 0
}

/**
 * Check if an object is not empty.
 */
export function is_set_object(elem: any): boolean {
	return elem && Object.keys(elem).length > 0
}

/**
 * Typed version of `is_set_array`.
 */
export function is_set_array_type<T>(elem: any): elem is Array<T> {
	return is_set_array(elem)
}

/**
 * Typed version of `is_set_object`.
 */
export function is_set_object_type<T>(elem: undefined | null | {} | T): elem is T {
	return is_set_object(elem)
}

/**
 * Capitalize a string.
 */
export function bx_capitalize(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Sort elements of an array by their 'name' key.
 */
export function sortByName<T>(arr: (T & { name: string })[]) {
	return [...arr].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Check if an array contains a product with the same "id" key.
 */
export function containsProduct(obj: BaseProduct, list: BaseProduct[]) {
	let obj_id = obj.id
	for (const i in list) {
		if (list[i].id === obj_id) {
			return true
		}
	}
	return false
}

/**
 * Equivalent of `parseFloat(x.toFixed(fractionDigits))`.
 * @param x
 * @param fractionDigits
 */
export function truncateNumber(x: number, fractionDigits: number) {
	return parseFloat(x.toFixed(fractionDigits))
}

/**
 * Test if an email is valid.
 */
export function validateEmail(email: string) {
	return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.[a-z]{2,})+$/.test(email)
}


/**
 * Get sort parameter to order category results
 */
export function orderCategoryResults(value: string): Record<string, { order: 'desc' | 'asc' }> {
	switch (value) {
		case 'relevance':
			return {}
		case 'price-high':
			return { final_price: { order: 'desc' } }
		case 'price-low':
			return { final_price: { order: 'asc' } }
		case 'rating':
			return {
				rating_average: { order: 'desc' },
				reviews_number: { order: 'desc' },
			}
		case 'date-new':
			return { created_at: { order: 'desc' } }
		case 'date-old':
			return { created_at: { order: 'asc' } }
		case 'default':
			return {}
		default:
			return {}
	}
}

/**
 * Get sequra status
 */
export function isSequraActive(settings: Settings, cat_ids: number[] = []) {
	if (settings.sequra) {
		const disabled_ids_string = settings.disabled_sequra_cats?.replace(' ', '').split(',')

		if (disabled_ids_string) {
			const disabled_ids = disabled_ids_string.map((e) => parseInt(e))
			let eq_ids = cat_ids.filter((val) => disabled_ids.includes(val))

			if (eq_ids.length) {
				return false
			}
		}
		return true
	}

	return false
}

/**
 * Chunk array in groups of n
 */
export function chunkArrayInGroups<T>(arr: Array<T>, n: number): Array<T>[] {
	const size = Math.ceil(arr.length / n)
	return Array.from({ length: size }, (_, i) => arr.slice(i * n, i * n + n))
}

/**
 * Get current universe info
 */
export function getUniverseInfo(universes: Universe[], cat_url: string) {
	if (!is_set_array(universes)) {
		return {}
	}
	let current_universe = universes.filter((universe) => universe.cat_urls === cat_url)
	return current_universe[0]
}

/*FORMAT REVIEWS DATA */

/*review.io */

/** The format of the reviews data from the reviews.io API have, among other things, this properties */
interface GetReviewsioReviews {
	average_rating: number
	verdict: string
	reviews: Array<{ rating: number; comments: string; author: { name: string }; time_ago: string }>
}

/** The function getFormatedDataReviewsio format this data so that we get something like that :
{
		platform: 'Trustpilot',
		platformLink: 'https://fr.trustpilot.com/review/bixoto.com',
		globalReview: 'Excellent service',
		rating: 4.5,
		reviews: [
			{
				name: 'Anónimo',
				rating: 4,
				comment:
					'Llevo años pidiendo aquí, pero hace unos años conocí a Pedro Gonzalez, técnico de Pc Componentes y el me ha ayudado en dos ocasiones a montar dos equipos gaming, con unas características muy buenas y dándome un servicio que ya muchas tiendas quisieran, la pasión que tiene como técnico de a la hora de ayudar a montar un pc que se adapte a tus necesidades no lo tiene mucha gente, trabajadores como el, son Edición Limitada. Envíos y resolución de cualquier problema 10/10'
			},
			{
				name: 'Juan Carlos Martin',
				rating: 4,
				comment:
					'Muy satisfecho en general, aunque en estos ultimos meses han tenido problemas con las empresas de paqueteria, puedo asegurar que se implican para darte solución a todo lo que este en su alcance. Almenos yo tuve problema grande y me ayudaron'
			}.......
		]
	};
 */
export function getFormatedDataReviewsio(reviewsio: GetReviewsioReviews) {
	return {
		platform: 'Reviews.io',
		platformLink: 'https://www.reviews.io/company-reviews/store/bixoto.com',
		global_review: reviewsio.verdict,
		average_rating: reviewsio.average_rating,
		reviews: reviewsio.reviews.map((review) => ({
			name: review.author.name,
			rating: review.rating,
			comment: review.comments,
		})),
	}
}

/*
 * Pagination -> function to the elements of arrays depending on the current Page
 */

export function getCurrentPageElement<T>(
	currentPage: number,
	array: T[],
	itemsPerPage: number,
): T[] {
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = Math.min(startIndex + itemsPerPage, array.length)
	const currentPageItemsIndex = Array.from(
		{ length: endIndex - startIndex },
		(_, index) => index + startIndex,
	)
	return currentPageItemsIndex.map((index) => array[index])
}

/*
 * Check if the customer is logged in
 */

export function isCustomerLoggedIn(token: { status: string } | null | undefined) {
	// `!!` to return a boolean
	return !!(token && token.status === 'logged_in')
}

/*
 * Close Session function
 */

export async function closeSession(options: { scope: SiteScope }) {
	// TODO[i18n]: use the scope
	await fetch(`${PUBLIC_MAGENTO_ENDPOINT}/es/customer/account/logout/`).then(() => {
		storeLoggedUser.set(false)
		storeCustomerToken.set({} as CustomerToken)
		storeActiveWishlist.set({} as Wishlist)
		storeWishlists.set([] as Wishlist[])
		goto(`/${options.scope}`)
	})
}


/*
 * Encode Secret Order Key
 */

const secret_magento_order_key_dict = (() => {
	const from_ = '+/abcdef0123456789',
		to_ = '-_0123456789abcdef',
		dict: Record<string, string> = {}

	for (let i = 0; i < from_.length; i++) {
		dict [from_[i]] = to_[i]
	}

	return dict
})()

// btoa doesn't support Unicode chars
// https://base64.guru/developers/javascript/examples/unicode-strings
function utoa(data: string) {
	return btoa(unescape(encodeURIComponent(data)))
}

export function encodeSecretOrderKey(email: string): string {
	const b64 = utoa(email).replace(/=+$/, '')
	let encoded = ''
	for (let c of b64) {
		encoded += secret_magento_order_key_dict[c] || c
	}

	return 'a' + encoded
}


/*
 * Decode Secret Order Key
 */

export function decodeSecretOrderKey(input: string | null) {
	if (!input || input[0] !== 'a') {
		return null
	}

	const CHAR_MAP: any = {
		'0': 'a',
		'1': 'b',
		'2': 'c',
		'3': 'd',
		'4': 'e',
		'5': 'f',
		'6': '0',
		'7': '1',
		'8': '2',
		'9': '3',
		'a': '4',
		'b': '5',
		'c': '6',
		'd': '7',
		'e': '8',
		'f': '9',
		'-': '+',
		'_': '/',
	}

	let base64 = ''

	for (let char of input.slice(1)) {
		base64 += CHAR_MAP[char] || char
	}

	return atob(base64) ? atob(base64) : undefined
}


/*
 * Check if Secret Order Key is good
 */
export function validateOrderKey(email: string) {
	let customerKey: string | null = page.url.searchParams.get('key')

	if (customerKey) {
		try {
			let decodedKey = decodeSecretOrderKey(customerKey)
			return decodedKey === email
		} catch (error) {
			return false
		}
	}

	return false
}


/*
 * Clean a search parameter in url
 */
export function cleanSearchParam(params: string[]) {
	for (let index = 0; index < params.length; index++) {
		const param = params[index]
		if (page.url.searchParams.has(param)) {
			page.url.searchParams.delete(param)
			replaceState(page.url, {})
			pushState(page.url, {})
		}
	}
}


/*
 * Set a parameter in url, and if value is undefined, clean this parameter from url
 */

export function setSearchParam(param: string, value: string | undefined) {
	if (value) {
		page.url.searchParams.set(param, value)
		replaceState(page.url, {})
		pushState(page.url, {})
	} else cleanSearchParam([param])
}


/*
 * Check Order status
 * TODO: clarify this name: what does 'check order status' mean?
 */
export function checkOrderStatus(status: string) {
	return (
		status === 'processing' ||
		status === 'closed' ||
		status === 'complete' ||
		status === 'awaiting_shipping'
	)
}


export function translateStatus(status: string) {
	if (status === 'Processed') {
		return gt`Processed`
	} else if (status === 'Closed') {
		return gt`Closed`
	} else if (status === 'Complete') {
		return gt`Complete`
	} else if (status === 'Canceled') {
		return gt`Canceled`
	} else if (status === 'Payment pending validation') {
		return gt`Payment pending validation`
	}

	return status
}

/*
 * Social login with Google
 */
export function socialLogin(type: 'google' | 'facebook') {
	const url = `${PUBLIC_MAGENTO_ENDPOINT}/sociallogin/social/login/type/${type}`
	const width = 500
	const height = 600
	const left = (window.innerWidth - width) / 2
	const top = (window.innerHeight - height) / 2

	window.open(
		url,
		`Login${type.charAt(0).toUpperCase() + type.slice(1)}`,
		`width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=no,status=no`,
	)
}

/*
 * Set product tags name
 */
export function setTagName(tag: ProductTag) {
	switch (tag) {
		case 'new':
			return gt`New`
		case 'hot_now':
			return gt`Hot`
		case 'best_rated':
			return gt`Best Rated`
		case 'most_sold':
			return gt`Most Sold`
	}
}

/*
 * Get product tags color
 */
export function getTagColor(tag: ProductTag) {
	switch (tag) {
		case 'new':
			return '#007AC5'
		case 'hot_now':
			return '#C50003'
		case 'best_rated':
			return '#3E9C57'
		case 'most_sold':
			return '#9C8E3E'
	}
}

/*
 * Get product tags icon
 */
export function getTagIcon(tag: ProductTag) {
	switch (tag) {
		case 'new':
			return 'new'
		case 'hot_now':
			return 'hot'
		case 'best_rated':
			return 'badge'
		case 'most_sold':
			return 'best-seller'
	}
}